import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  PenTool, 
  Type, 
  Calendar, 
  CheckSquare, 
  Mail, 
  Phone,
  User,
  ZoomIn,
  ZoomOut,
  Eye,
  Edit3,
  Save,
  Trash2,
  Settings,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { 
  FormTemplate, 
  SignatureField, 
  FormField, 
  TemplateSettings,
  TemplateDesignerState 
} from '@/types/documentRequestTypes';

interface TemplateDesignerProps {
  template?: FormTemplate;
  onSave: (template: Partial<FormTemplate>) => void;
  onCancel: () => void;
}

const TemplateDesigner: React.FC<TemplateDesignerProps> = ({
  template,
  onSave,
  onCancel
}) => {
  const { user } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  
  // Template metadata
  const [templateName, setTemplateName] = useState(template?.name || '');
  const [templateDescription, setTemplateDescription] = useState(template?.description || '');
  const [templateCategory, setTemplateCategory] = useState<FormTemplate['category']>(template?.category || 'other');
  
  // Designer state
  const [designerState, setDesignerState] = useState<TemplateDesignerState>({
    template_id: template?.id,
    pdf_url: '',
    pdf_pages: 1,
    current_page: 1,
    zoom_level: 1,
    signature_fields: template?.signature_fields || [],
    form_fields: template?.form_fields || [],
    is_preview_mode: false
  });

  // Load PDF URL (convert file path to signed URL if needed)
  useEffect(() => {
    const loadPdfUrl = async () => {
      if (!template?.template_file_url) return;
      
      let pdfUrl = template.template_file_url;
      
      // If it's a file path (not a full URL), create a signed URL
      if (!pdfUrl.startsWith('http') && !pdfUrl.startsWith('blob:')) {
        try {
          const { data: signedUrlData, error } = await supabase.storage
            .from('form-templates')
            .createSignedUrl(pdfUrl, 3600); // 1 hour expiry
          
          if (error) {
            console.error('Failed to create signed URL:', error);
            toast.error('Failed to load PDF file');
            return;
          }
          
          console.log('Created signed URL:', signedUrlData.signedUrl);
          pdfUrl = signedUrlData.signedUrl;
        } catch (error) {
          console.error('Error creating signed URL:', error);
          toast.error('Failed to load PDF file');
          return;
        }
      }
      
      setDesignerState(prev => ({ ...prev, pdf_url: pdfUrl }));
    };
    
    loadPdfUrl();
  }, [template?.template_file_url]);
  
  // UI state
  const [selectedTool, setSelectedTool] = useState<'signature' | 'initial' | 'date' | 'text' | 'email' | 'phone' | 'checkbox'>('signature');
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [isPlacingField, setIsPlacingField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<'se' | 'sw' | 'ne' | 'nw' | null>(null);
  
  // Template settings
  const [templateSettings, setTemplateSettings] = useState<TemplateSettings>(
    template?.template_settings || {
      signing_order: 'parallel',
      expiration_days: 30,
      reminder_schedule: [1, 3, 7],
      require_authentication: false,
      allow_decline: true,
      brand_color: '#3B82F6'
    }
  );

  // Field types configuration
  const fieldTypes = {
    signature: { icon: PenTool, label: 'Signature', color: 'blue' },
    initial: { icon: User, label: 'Initials', color: 'green' },
    date: { icon: Calendar, label: 'Date Signed', color: 'purple' },
    text: { icon: Type, label: 'Text Input', color: 'gray' },
    email: { icon: Mail, label: 'Email', color: 'orange' },
    phone: { icon: Phone, label: 'Phone', color: 'red' },
    checkbox: { icon: CheckSquare, label: 'Checkbox', color: 'teal' }
  };

  // Generate unique field ID
  const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Helper functions for consistent color classes
  const getFieldBorderColor = (color: string) => {
    const colorMap = {
      blue: 'border-blue-400 bg-blue-100/30',
      green: 'border-green-400 bg-green-100/30',
      purple: 'border-purple-400 bg-purple-100/30',
      gray: 'border-gray-400 bg-gray-100/30',
      orange: 'border-orange-400 bg-orange-100/30',
      red: 'border-red-400 bg-red-100/30',
      teal: 'border-teal-400 bg-teal-100/30'
    };
    return colorMap[color as keyof typeof colorMap] || 'border-gray-400 bg-gray-100/30';
  };

  const getFieldIconColor = (color: string) => {
    const colorMap = {
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      gray: 'text-gray-600',
      orange: 'text-orange-600',
      red: 'text-red-600',
      teal: 'text-teal-600'
    };
    return colorMap[color as keyof typeof colorMap] || 'text-gray-600';
  };

  // Get field by ID
  const getField = useCallback((fieldId: string): SignatureField | FormField | undefined => {
    return [...designerState.signature_fields, ...designerState.form_fields]
      .find(f => f.id === fieldId);
  }, [designerState.signature_fields, designerState.form_fields]);

  // Update field properties
  const updateField = useCallback((fieldId: string, updates: Partial<SignatureField | FormField>) => {
    setDesignerState(prev => ({
      ...prev,
      signature_fields: prev.signature_fields.map(f => 
        f.id === fieldId ? { ...f, ...updates } : f
      ),
      form_fields: prev.form_fields.map(f => 
        f.id === fieldId ? { ...f, ...updates } : f
      )
    }));
  }, []);

  // Handle mouse events for field dragging and resizing
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!selectedField || !pdfContainerRef.current) return;

    const container = pdfContainerRef.current;
    const rect = container.getBoundingClientRect();
    
    if (isDragging) {
      const x = Math.max(0, Math.min(100, ((event.clientX - rect.left - dragOffset.x) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((event.clientY - rect.top - dragOffset.y) / rect.height) * 100));
      updateField(selectedField, { x, y });
    } else if (isResizing && resizeHandle) {
      const field = getField(selectedField);
      if (!field) return;

      const mouseX = ((event.clientX - rect.left) / rect.width) * 100;
      const mouseY = ((event.clientY - rect.top) / rect.height) * 100;

      let newWidth = field.width;
      let newHeight = field.height;
      let newX = field.x;
      let newY = field.y;

      switch (resizeHandle) {
        case 'se': // Southeast - resize width and height
          newWidth = Math.max(5, mouseX - field.x);
          newHeight = Math.max(3, mouseY - field.y);
          break;
        case 'sw': // Southwest - resize width and height, adjust x
          newWidth = Math.max(5, field.x + field.width - mouseX);
          newHeight = Math.max(3, mouseY - field.y);
          newX = Math.max(0, mouseX);
          break;
        case 'ne': // Northeast - resize width and height, adjust y
          newWidth = Math.max(5, mouseX - field.x);
          newHeight = Math.max(3, field.y + field.height - mouseY);
          newY = Math.max(0, mouseY);
          break;
        case 'nw': // Northwest - resize width and height, adjust x and y
          newWidth = Math.max(5, field.x + field.width - mouseX);
          newHeight = Math.max(3, field.y + field.height - mouseY);
          newX = Math.max(0, mouseX);
          newY = Math.max(0, mouseY);
          break;
      }

      updateField(selectedField, { 
        x: newX, 
        y: newY, 
        width: Math.min(newWidth, 100 - newX), 
        height: Math.min(newHeight, 100 - newY) 
      });
    }
  }, [isDragging, isResizing, selectedField, dragOffset, resizeHandle, updateField, getField]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  // Add global mouse event listeners for dragging and resizing
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Handle canvas click to place fields
  const handleCanvasClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isPlacingField || !pdfContainerRef.current) return;

    const container = pdfContainerRef.current;
    const rect = container.getBoundingClientRect();
    
    // Calculate position relative to the PDF container
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    const newField = {
      id: generateFieldId(),
      label: `${fieldTypes[selectedTool].label} ${designerState.signature_fields.length + designerState.form_fields.length + 1}`,
      x,
      y,
      width: selectedTool === 'signature' ? 25 : selectedTool === 'checkbox' ? 3 : 20,
      height: selectedTool === 'signature' ? 8 : selectedTool === 'checkbox' ? 3 : 5,
      page: designerState.current_page,
      required: true
    };

    if (['signature', 'initial', 'date'].includes(selectedTool)) {
      const signatureField: SignatureField = {
        ...newField,
        signer_role: 'parent',
        field_type: selectedTool as 'signature' | 'initial' | 'date_signed'
      };
      
      setDesignerState(prev => ({
        ...prev,
        signature_fields: [...prev.signature_fields, signatureField]
      }));
    } else {
      const formField: FormField = {
        ...newField,
        field_type: selectedTool as 'text' | 'email' | 'phone' | 'checkbox',
        placeholder: selectedTool === 'email' ? 'Enter email address' : 
                    selectedTool === 'phone' ? 'Enter phone number' : 
                    selectedTool === 'text' ? 'Enter text' : undefined
      };
      
      setDesignerState(prev => ({
        ...prev,
        form_fields: [...prev.form_fields, formField]
      }));
    }

    setIsPlacingField(false);
    toast.success(`${fieldTypes[selectedTool].label} field placed`);
  }, [isPlacingField, selectedTool, designerState.current_page, designerState.signature_fields.length, designerState.form_fields.length]);

  // Remove field
  const removeField = (fieldId: string) => {
    setDesignerState(prev => ({
      ...prev,
      signature_fields: prev.signature_fields.filter(f => f.id !== fieldId),
      form_fields: prev.form_fields.filter(f => f.id !== fieldId)
    }));
    setSelectedField(null);
    toast.success('Field removed');
  };



  // Save template
  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setLoading(true);
    try {
      const templateData: Partial<FormTemplate> = {
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        category: templateCategory,
        signature_fields: designerState.signature_fields,
        form_fields: designerState.form_fields,
        template_settings: templateSettings
      };

      await onSave(templateData);
      toast.success('Template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  // Toggle preview mode
  const togglePreviewMode = () => {
    setDesignerState(prev => ({
      ...prev,
      is_preview_mode: !prev.is_preview_mode
    }));
    setSelectedField(null);
  };

  // Render PDF with overlay fields
  const renderPDFWithFields = () => {
    if (!designerState.pdf_url) return null;

    return (
      <div 
        ref={pdfContainerRef}
        className="relative border border-gray-300 bg-white overflow-hidden"
        style={{ height: '600px' }}
      >
        {/* PDF Display */}
        <object
          data={`${designerState.pdf_url}#page=${designerState.current_page}&zoom=${designerState.zoom_level * 100}`}
          type="application/pdf"
          className="w-full h-full"
          style={{ pointerEvents: designerState.is_preview_mode ? 'none' : 'auto' }}
        >
          <embed
            src={`${designerState.pdf_url}#page=${designerState.current_page}&zoom=${designerState.zoom_level * 100}`}
            type="application/pdf"
            className="w-full h-full"
          />
        </object>
        
        {/* Field Overlay - Click handler */}
        {isPlacingField && (
          <div
            className="absolute top-0 left-0 w-full h-full cursor-crosshair"
            style={{ zIndex: 10 }}
            onClick={handleCanvasClick}
          />
        )}
        
        {/* Field Overlays */}
        {[...designerState.signature_fields, ...designerState.form_fields]
          .filter(field => field.page === designerState.current_page)
          .map(field => {
            const fieldType = fieldTypes[field.field_type as keyof typeof fieldTypes];
            const isSelected = selectedField === field.id;
            
            if (designerState.is_preview_mode) {
              // Preview mode - show actual form fields
              return (
                <div
                  key={field.id}
                  className="absolute"
                  style={{
                    left: `${field.x}%`,
                    top: `${field.y}%`,
                    width: `${field.width}%`,
                    height: `${field.height}%`,
                    zIndex: 20
                  }}
                >
                  {/* Render actual form inputs for preview */}
                  {field.field_type === 'signature' && (
                    <div className="w-full h-full border-2 border-gray-300 bg-gray-50 rounded flex items-center justify-center text-gray-500 text-xs">
                      {field.required && <span className="text-red-500 mr-1">*</span>}
                      Click to sign
                    </div>
                  )}
                  {field.field_type === 'initial' && (
                    <div className="w-full h-full border-2 border-gray-300 bg-gray-50 rounded flex items-center justify-center text-gray-500 text-xs">
                      {field.required && <span className="text-red-500 mr-1">*</span>}
                      Initials
                    </div>
                  )}
                  {field.field_type === 'date_signed' && (
                    <div className="w-full h-full border-2 border-gray-300 bg-gray-50 rounded flex items-center justify-center text-gray-500 text-xs">
                      {field.required && <span className="text-red-500 mr-1">*</span>}
                      {new Date().toLocaleDateString()}
                    </div>
                  )}
                  {['text', 'email', 'phone'].includes(field.field_type) && (
                    <input
                      type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
                      placeholder={('placeholder' in field) ? field.placeholder : field.label}
                      className="w-full h-full border-2 border-gray-300 rounded px-2 text-sm"
                      disabled
                    />
                  )}
                  {field.field_type === 'checkbox' && (
                    <div className="flex items-center h-full">
                      <input type="checkbox" className="mr-2" disabled />
                      <span className="text-xs">{field.label}</span>
                    </div>
                  )}
                </div>
              );
            } else {
              // Edit mode - show design overlays
              return (
                <div
                  key={field.id}
                  className={`absolute border-2 border-dashed rounded transition-all ${
                    isSelected ? 'border-blue-500 bg-blue-100/50 cursor-move' : `${getFieldBorderColor(fieldType.color)} cursor-pointer`
                  } hover:bg-opacity-50`}
                  style={{
                    left: `${field.x}%`,
                    top: `${field.y}%`,
                    width: `${field.width}%`,
                    height: `${field.height}%`,
                    zIndex: 20
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedField(field.id);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setSelectedField(field.id);
                    setIsDragging(true);
                    
                    // Calculate drag offset relative to field position
                    const fieldRect = e.currentTarget.getBoundingClientRect();
                    setDragOffset({
                      x: e.clientX - fieldRect.left,
                      y: e.clientY - fieldRect.top
                    });
                  }}
                >
                  <div className="absolute -top-6 left-0 text-xs bg-white px-1 rounded shadow">
                    {field.label}
                  </div>
                  {fieldType.icon && (
                    <fieldType.icon className={`absolute top-1 right-1 h-3 w-3 ${getFieldIconColor(fieldType.color)}`} />
                  )}
                  
                  {/* Resize handles - only show for selected field */}
                  {isSelected && (
                    <>
                      {/* Southeast handle */}
                      <div
                        className="absolute bottom-0 right-0 w-3 h-3 bg-blue-500 cursor-se-resize transform translate-x-1 translate-y-1"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setIsResizing(true);
                          setResizeHandle('se');
                        }}
                      />
                      {/* Southwest handle */}
                      <div
                        className="absolute bottom-0 left-0 w-3 h-3 bg-blue-500 cursor-sw-resize transform -translate-x-1 translate-y-1"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setIsResizing(true);
                          setResizeHandle('sw');
                        }}
                      />
                      {/* Northeast handle */}
                      <div
                        className="absolute top-0 right-0 w-3 h-3 bg-blue-500 cursor-ne-resize transform translate-x-1 -translate-y-1"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setIsResizing(true);
                          setResizeHandle('ne');
                        }}
                      />
                      {/* Northwest handle */}
                      <div
                        className="absolute top-0 left-0 w-3 h-3 bg-blue-500 cursor-nw-resize transform -translate-x-1 -translate-y-1"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setIsResizing(true);
                          setResizeHandle('nw');
                        }}
                      />
                    </>
                  )}
                </div>
              );
            }
          })}
      </div>
    );
  };

  const selectedFieldData = selectedField ? getField(selectedField) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Template Designer</h2>
          <p className="text-gray-600">Create interactive forms with signature and input fields</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={togglePreviewMode}>
            <Eye className="h-4 w-4 mr-2" />
            {designerState.is_preview_mode ? 'Edit Mode' : 'Preview Mode'}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-12rem)]">
        {/* Toolbar */}
        <div className="lg:col-span-1 space-y-4 overflow-y-auto pr-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Template Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label htmlFor="template-name">Name</Label>
                <Input
                  id="template-name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Medical Release Form"
                />
              </div>
              <div>
                <Label htmlFor="template-description">Description</Label>
                <Textarea
                  id="template-description"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Brief description..."
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="template-category">Category</Label>
                <Select value={templateCategory} onValueChange={(value: FormTemplate['category']) => setTemplateCategory(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="waiver">Waiver</SelectItem>
                    <SelectItem value="medical">Medical</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                    <SelectItem value="enrollment">Enrollment</SelectItem>
                    <SelectItem value="media">Media Release</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {!designerState.is_preview_mode && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Add Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(fieldTypes).map(([type, config]) => (
                  <Button
                    key={type}
                    variant={selectedTool === type ? "default" : "outline"}
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => {
                      setSelectedTool(type as any);
                      setIsPlacingField(true);
                      toast.info(`Click on the document to place a ${config.label} field`);
                    }}
                  >
                    <config.icon className="h-4 w-4 mr-2" />
                    {config.label}
                  </Button>
                ))}
                
                {isPlacingField && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription className="flex items-center justify-between">
                      <span>Click where you want to place the {fieldTypes[selectedTool].label} field</span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setIsPlacingField(false)}
                      >
                        Cancel
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {selectedFieldData && !designerState.is_preview_mode && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center justify-between">
                  Field Properties
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeField(selectedField!)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Label</Label>
                  <Input
                    value={selectedFieldData.label}
                    onChange={(e) => updateField(selectedField!, { label: e.target.value })}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="required"
                    checked={selectedFieldData.required}
                    onCheckedChange={(checked) => 
                      updateField(selectedField!, { required: checked as boolean })
                    }
                  />
                  <Label htmlFor="required">Required</Label>
                </div>

                {('signer_role' in selectedFieldData) && (
                  <div>
                    <Label>Signer Role</Label>
                    <Select 
                      value={selectedFieldData.signer_role} 
                      onValueChange={(value) => updateField(selectedField!, { signer_role: value as SignatureField['signer_role'] })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="parent">Parent</SelectItem>
                        <SelectItem value="guardian">Guardian</SelectItem>
                        <SelectItem value="emergency_contact">Emergency Contact</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {('placeholder' in selectedFieldData) && (
                  <div>
                    <Label>Placeholder</Label>
                    <Input
                      value={selectedFieldData.placeholder || ''}
                      onChange={(e) => updateField(selectedField!, { placeholder: e.target.value })}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* PDF Canvas */}
        <div className="lg:col-span-3 overflow-hidden">
          <Card className="h-full flex flex-col">
            <CardHeader className="flex-shrink-0">
              <CardTitle>Document Preview</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto p-4">
              {renderPDFWithFields()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TemplateDesigner;